import React from "react";
import type { ProjectRecord } from "../../backend/api/generated/openapi";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useAccessibleProjects } from "../../backend/queries/projectContent";
import { useCurrentUser } from "../../backend/queries/auth";
import {
  Page,
  PageHeader,
  PageSection,
  DataView,
  EmptyState,
} from "../../components/patterns";
import { ProjectStatusBadge } from "../../components/domain-badges";
import { Button } from "../../components/ui/button";

export default function ClientOverview() {
  const projects = useAccessibleProjects();
  const { data: user } = useCurrentUser();
  const [params] = useSearchParams();
  const location = useLocation();
  const oldProject = params.get("projectId") || location.state?.projectId;
  if (oldProject)
    return (
      <Navigate
        replace
        to={`/my-projects/${encodeURIComponent(oldProject)}`}
        state={location.state}
      />
    );
  return (
    <Page>
      <PageHeader
        title="Your relocation"
        description={`Welcome${user?.profile?.firstname ? `, ${user.profile.firstname}` : ""}. Your projects and everything you need for your move.`}
      />
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_15rem]">
        <PageSection
          title="Your projects"
          description="Open a project to see its progress and next steps."
        >
          <DataView<ProjectRecord>
            caption="Your projects"
            rows={projects.data || []}
            getRowKey={(p) => p.id}
            status={
              projects.isPending
                ? "loading"
                : projects.isError
                  ? "error"
                  : "ready"
            }
            onRetry={() => projects.refetch()}
            getRowHref={(p) => `/my-projects/${p.id}`}
            empty={
              <EmptyState
                title="Your next chapter starts here"
                description="Your team will share your project when it is ready."
              />
            }
            columns={[
              {
                id: "project",
                header: "Project",
                primary: true,
                cell: (p) => (
                  <span className="font-medium">
                    {p.relocating_from || "Your move"} →{" "}
                    {p.relocating_to || `Project ${p.id}`}
                  </span>
                ),
              },
              {
                id: "status",
                header: "Status",
                cell: (p) => <ProjectStatusBadge status={p.status} />,
              },
            ]}
          />
        </PageSection>
        <PageSection title="For your move">
          <div className="flex flex-col gap-2 rounded-lg border bg-card p-3">
            {[
              ["Properties & viewings", "/properties"],
              ["Project updates", "/project-updates"],
              ["Documents", "/documents"],
              ["Costs & payments", "/budgeting"],
            ].map(([label, to]) => (
              <Button
                key={to}
                asChild
                variant="ghost"
                className="justify-start"
              >
                <Link to={to}>{label}</Link>
              </Button>
            ))}
          </div>
        </PageSection>
      </div>
    </Page>
  );
}
